import { useStyletron } from '@tigergraph/app-ui-lib/Theme';
import { Label } from '@tigergraph/app-ui-lib/typography';
import { Clock } from 'lucide-react';
import { styled } from '@tigergraph/app-ui-lib/Theme';
import { TimezonePicker } from '@tigergraph/app-ui-lib/timezonepicker';
import { Select } from '@tigergraph/app-ui-lib/select';
import { UseFormReturn } from 'react-hook-form';
import { type AdvancedType } from '@/pages/workgroup/form/WorkSpaceSubForm';
import { FormTitleContainer } from '@/components/styled';
import TooltipLabel from '@/components/TooltipLabel';
import { Checkbox } from '@tigergraph/app-ui-lib/checkbox';
import { STYLE_TYPE } from 'baseui/checkbox';
import { getSupportMailLink } from '@/utils/utils';
import { useOrgContext } from '@/contexts/orgContext';
import mediaQuery from '@/lib/breakpoints';

export const Title = styled('div', ({ $theme }) => {
  return {
    marginBottom: '8px',
    fontWeight: 500,
    color: $theme.colors['dropdown.text'],
  };
});

export default function Maintenance({ form }: { form: UseFormReturn<AdvancedType, any, undefined> }) {
  const [css, theme] = useStyletron();

  const { setValue, watch } = form;

  const updateStrategy = watch('update_strategy');
  const startHour = watch('maintenance_window_hour_of_day');
  const day = watch('maintenance_window_day_of_week');
  const timezone = watch('maintenance_window_time_zone');

  const { currentOrg, userInfo } = useOrgContext();
  const url = getSupportMailLink({ userInfo, currentOrg, subject: 'Automatic Upgrade' });

  return (
    <>
      <FormTitleContainer
        className={css({
          marginTop: '8px',
        })}
      >
        <TooltipLabel
          label="Automatic Upgrade"
          tooltip={`To ensure you always have the latest features and security improvements, your workspace will automatically upgrade to the most recent stable version—only thoroughly tested releases are deployed.`}
        />
        <Checkbox
          checked={updateStrategy === 'update_strategy_window'}
          labelPlacement="left"
          onChange={() => {
            setValue(
              'update_strategy',
              updateStrategy === 'update_strategy_window' ? 'update_strategy_never' : 'update_strategy_window'
            );
          }}
          checkmarkType={STYLE_TYPE.toggle_round}
        />
      </FormTitleContainer>
      <div
        className={css({
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'stretch',
          gap: '16px',
        })}
      >
        {updateStrategy === 'update_strategy_window' && (
          <>
            <div
              className={css({
                padding: '16px 20px',
                color: theme.colors['text.secondary'],
                borderRadius: '4px',
                border: `1px solid ${theme.colors['border.tertiary']}`,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'stretch',
                gap: '4px',
              })}
            >
              <div
                className={css({
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                })}
              >
                <Clock size={20} color={theme.colors['icon.primary']} />
                <span>
                  An update is scheduled every{' '}
                  <span
                    className={css({
                      color: theme.colors['text.primary'],
                      fontWeight: 'bold',
                    })}
                  >
                    {days.find((d) => d.id === day)?.label}
                  </span>{' '}
                  at{' '}
                  <span
                    className={css({
                      color: theme.colors['text.primary'],
                      fontWeight: 'bold',
                    })}
                  >
                    {formatHour(startHour)}{' '}
                  </span>{' '}
                  {timezone}
                </span>
              </div>
              <span
                className={css({
                  fontSize: '12px',
                  lineHeight: '16px',
                })}
              >
                The upgrade process may take over 2 hours to complete. If it exceeds this timeframe, please don’t
                hesitate to{' '}
                <a
                  href={url}
                  target="_blank"
                  rel="noreferrer"
                  className={css({
                    color: theme.colors.secondary800,
                  })}
                >
                  contact us
                </a>{' '}
                for assistance.
                <br />
                The workspace needs to be in an active state to perform automatic upgrade.
              </span>
            </div>
            <div>
              <Title>Day of the week</Title>
              <div
                className={css({
                  display: 'flex',
                  gap: '16px',
                  [mediaQuery.xsmall]: {
                    gap: '8px',
                  },
                })}
              >
                {days.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    className={css({
                      lineHeight: '16px',
                      height: '32px',
                      width: '32px',
                      borderRadius: '50%',
                      flexShrink: 0,
                      textAlign: 'center',
                      color: d.id === day ? theme.colors['datepicker.text.selected'] : theme.colors['datepicker.text'],
                      backgroundColor:
                        d.id === day
                          ? theme.colors['datepicker.background.selected']
                          : theme.colors['datepicker.background.hover'],
                    })}
                    onClick={() => setValue('maintenance_window_day_of_week', d.id)}
                  >
                    {d.label.slice(0, 2)}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Title>Timezone</Title>
              <div
                className={css({
                  width: '320px',
                  [mediaQuery.xsmall]: {
                    width: '100%',
                  },
                })}
              >
                <TimezonePicker value={timezone} onChange={({ id }) => setValue('maintenance_window_time_zone', id)} />
              </div>
            </div>
            <div>
              <Title>Hour</Title>
              <div
                className={css({
                  width: '320px',
                  [mediaQuery.xsmall]: {
                    width: '100%',
                  },
                })}
              >
                <Select
                  value={[{ id: startHour }]}
                  onChange={(params) => {
                    setValue('maintenance_window_hour_of_day', params.value[0].id as number);
                  }}
                  options={hours}
                  maxDropdownHeight="400px"
                  clearable={false}
                />
              </div>
              <Label
                className={css({
                  color: theme.colors['text.secondary'],
                  marginTop: '8px',
                })}
              >
                {formatHour(startHour)} in your local time.
              </Label>
            </div>
          </>
        )}
      </div>
    </>
  );
}

const days = [
  {
    id: 0,
    label: 'Sunday',
  },
  {
    id: 1,
    label: 'Monday',
  },
  {
    id: 2,
    label: 'Tuesday',
  },
  {
    id: 3,
    label: 'Wednesday',
  },
  {
    id: 4,
    label: 'Thursday',
  },
  {
    id: 5,
    label: 'Friday',
  },
  {
    id: 6,
    label: 'Saturday',
  },
];

const hours = Array.from({ length: 24 }, (_, i) => i).map((i) => ({
  id: i,
  label: formatHour(i),
}));

// generate a two hour window
function formatHour(hour: number) {
  return `${hour.toString().padStart(2, '0')}:00 - ${((hour + 2) % 24).toString().padStart(2, '0')}:00`;
}
